/**
 * Reimbursement Module API client
 */

import apiClient from '@/core/api/client'
import { API_ENDPOINTS } from '@/core/api/endpoints'
import type {
  ExpenseCategory,
  ReimbursementPolicy,
  ReimbursementClaim,
  ClaimItem,
  ClaimReceipt,
  CreateClaimInput,
  UpdateClaimInput,
  CreateClaimItemInput,
  UpdateClaimItemInput,
  ApprovalActionInput,
  RejectActionInput,
  ReimbursementSummary,
  CreatePolicyInput,
  UpdatePolicyInput,
  ApprovalThreshold,
  CreateApprovalThresholdInput,
  UpdateApprovalThresholdInput,
  ComprehensiveReport,
  ReportFilters,
  ReportEmployee,
  ReportDepartment,
} from '../types/reimbursement.types'
import type { ApiResponse, PaginatedResponse } from '@/shared/types/common.types'

export const reimbursementApi = {
  // Expense Categories
  getCategories: async () => {
    const response = await apiClient.get<ApiResponse<ExpenseCategory[]>>(
      API_ENDPOINTS.REIMBURSEMENT.CATEGORIES
    )
    return response.data
  },

  // Reimbursement Policies
  getPolicies: async (categoryId?: string) => {
    const params = categoryId ? { category_id: categoryId } : {}
    const response = await apiClient.get<ApiResponse<ReimbursementPolicy[]>>(
      API_ENDPOINTS.REIMBURSEMENT.POLICIES,
      { params }
    )
    return response.data
  },

  // Claims
  getClaims: async (params?: {
    status?: string
    page?: number
    limit?: number
    period_year?: number
    period_month?: number
  }) => {
    const response = await apiClient.get<PaginatedResponse<ReimbursementClaim>>(
      API_ENDPOINTS.REIMBURSEMENT.CLAIMS,
      { params }
    )
    return response.data
  },

  getClaim: async (id: string) => {
    const response = await apiClient.get<ApiResponse<ReimbursementClaim>>(
      API_ENDPOINTS.REIMBURSEMENT.CLAIM(id)
    )
    return response.data
  },

  createClaim: async (data: CreateClaimInput) => {
    const response = await apiClient.post<ApiResponse<ReimbursementClaim>>(
      API_ENDPOINTS.REIMBURSEMENT.CLAIMS,
      data
    )
    return response.data
  },

  updateClaim: async (id: string, data: UpdateClaimInput) => {
    const response = await apiClient.put<ApiResponse<ReimbursementClaim>>(
      API_ENDPOINTS.REIMBURSEMENT.CLAIM(id),
      data
    )
    return response.data
  },

  submitClaim: async (id: string) => {
    const response = await apiClient.post<ApiResponse<ReimbursementClaim>>(
      API_ENDPOINTS.REIMBURSEMENT.SUBMIT(id),
      { acknowledge_policy: true }
    )
    return response.data
  },

  cancelClaim: async (id: string) => {
    const response = await apiClient.post<ApiResponse<ReimbursementClaim>>(
      API_ENDPOINTS.REIMBURSEMENT.CANCEL(id)
    )
    return response.data
  },

  // Claim Items
  addClaimItem: async (claimId: string, data: CreateClaimItemInput) => {
    const response = await apiClient.post<ApiResponse<ClaimItem>>(
      API_ENDPOINTS.REIMBURSEMENT.CLAIM_ITEMS(claimId),
      data
    )
    return response.data
  },

  updateClaimItem: async (claimId: string, itemId: string, data: UpdateClaimItemInput) => {
    const response = await apiClient.put<ApiResponse<ClaimItem>>(
      API_ENDPOINTS.REIMBURSEMENT.CLAIM_ITEM(claimId, itemId),
      data
    )
    return response.data
  },

  deleteClaimItem: async (claimId: string, itemId: string) => {
    const response = await apiClient.delete<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.REIMBURSEMENT.CLAIM_ITEM(claimId, itemId)
    )
    return response.data
  },

  // Approvals
  getPendingApprovals: async () => {
    const response = await apiClient.get<ApiResponse<ReimbursementClaim[]>>(
      API_ENDPOINTS.REIMBURSEMENT.PENDING_APPROVALS
    )
    return response.data
  },

  approveClaim: async (id: string, data?: ApprovalActionInput) => {
    const response = await apiClient.post<ApiResponse<ReimbursementClaim>>(
      API_ENDPOINTS.REIMBURSEMENT.APPROVE(id),
      data || {}
    )
    return response.data
  },

  rejectClaim: async (id: string, data: RejectActionInput) => {
    const response = await apiClient.post<ApiResponse<ReimbursementClaim>>(
      API_ENDPOINTS.REIMBURSEMENT.REJECT(id),
      data
    )
    return response.data
  },

  // Payments
  getClaimsReadyForPayment: async () => {
    const response = await apiClient.get<ApiResponse<ReimbursementClaim[]>>(
      API_ENDPOINTS.REIMBURSEMENT.PAYMENTS_READY
    )
    return response.data
  },

  markClaimPaid: async (id: string, data?: { transaction_reference?: string }) => {
    const response = await apiClient.post<ApiResponse<ReimbursementClaim>>(
      API_ENDPOINTS.REIMBURSEMENT.MARK_PAID(id),
      data || {}
    )
    return response.data
  },

  getPaidClaims: async (params?: {
    page?: number
    limit?: number
    period_year?: number
    period_month?: number
    search?: string
    sort_by?: string
    sort_order?: string
  }) => {
    const response = await apiClient.get<PaginatedResponse<ReimbursementClaim>>(
      API_ENDPOINTS.REIMBURSEMENT.PAYMENTS_HISTORY,
      { params }
    )
    return response.data
  },

  // Reports
  getSummaryReport: async (params?: {
    period_year?: number
    period_month?: number
    user_id?: string
  }) => {
    const response = await apiClient.get<ApiResponse<ReimbursementSummary>>(
      API_ENDPOINTS.REIMBURSEMENT.REPORT_SUMMARY,
      { params }
    )
    return response.data
  },

  getComprehensiveReport: async (filters?: ReportFilters) => {
    const response = await apiClient.get<ApiResponse<ComprehensiveReport>>(
      API_ENDPOINTS.REIMBURSEMENT.REPORT_COMPREHENSIVE,
      { params: filters }
    )
    return response.data
  },

  getReportEmployees: async (search?: string) => {
    const params = search ? { search } : {}
    const response = await apiClient.get<ApiResponse<ReportEmployee[]>>(
      API_ENDPOINTS.REIMBURSEMENT.REPORT_EMPLOYEES,
      { params }
    )
    return response.data
  },

  getReportDepartments: async () => {
    const response = await apiClient.get<ApiResponse<ReportDepartment[]>>(
      API_ENDPOINTS.REIMBURSEMENT.REPORT_DEPARTMENTS
    )
    return response.data
  },

  getReportClaims: async (params?: ReportFilters & {
    page?: number
    limit?: number
    sort_by?: string
    sort_order?: string
  }) => {
    const response = await apiClient.get<PaginatedResponse<ReimbursementClaim>>(
      API_ENDPOINTS.REIMBURSEMENT.REPORT_CLAIMS,
      { params }
    )
    return response.data
  },

  // Receipts
  uploadReceipt: async (claimId: string, itemId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post<ApiResponse<ClaimReceipt>>(
      API_ENDPOINTS.REIMBURSEMENT.ITEM_RECEIPTS(claimId, itemId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  },

  deleteReceipt: async (claimId: string, itemId: string, receiptId: string) => {
    const response = await apiClient.delete<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.REIMBURSEMENT.ITEM_RECEIPT(claimId, itemId, receiptId)
    )
    return response.data
  },

  downloadReceipt: async (receiptId: string, fileName: string) => {
    const response = await apiClient.get(
      API_ENDPOINTS.REIMBURSEMENT.RECEIPT_DOWNLOAD(receiptId),
      { responseType: 'blob' }
    )

    // Create a blob URL and trigger download
    const blob = new Blob([response.data])
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },

  // Policy Admin
  createPolicy: async (data: CreatePolicyInput) => {
    const response = await apiClient.post<ApiResponse<ReimbursementPolicy>>(
      API_ENDPOINTS.REIMBURSEMENT.ADMIN_POLICY_CREATE,
      data
    )
    return response.data
  },

  updatePolicy: async (id: string, data: UpdatePolicyInput) => {
    const response = await apiClient.put<ApiResponse<ReimbursementPolicy>>(
      API_ENDPOINTS.REIMBURSEMENT.ADMIN_POLICY(id),
      data
    )
    return response.data
  },

  deletePolicy: async (id: string) => {
    const response = await apiClient.delete<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.REIMBURSEMENT.ADMIN_POLICY(id)
    )
    return response.data
  },

  // Approval Thresholds Admin
  getApprovalThresholds: async (activeOnly = false) => {
    const response = await apiClient.get<ApiResponse<ApprovalThreshold[]>>(
      API_ENDPOINTS.REIMBURSEMENT.ADMIN_APPROVAL_THRESHOLDS,
      { params: { active_only: activeOnly } }
    )
    return response.data
  },

  getApprovalThreshold: async (id: string) => {
    const response = await apiClient.get<ApiResponse<ApprovalThreshold>>(
      API_ENDPOINTS.REIMBURSEMENT.ADMIN_APPROVAL_THRESHOLD(id)
    )
    return response.data
  },

  createApprovalThreshold: async (data: CreateApprovalThresholdInput) => {
    const response = await apiClient.post<ApiResponse<ApprovalThreshold>>(
      API_ENDPOINTS.REIMBURSEMENT.ADMIN_APPROVAL_THRESHOLDS,
      data
    )
    return response.data
  },

  updateApprovalThreshold: async (id: string, data: UpdateApprovalThresholdInput) => {
    const response = await apiClient.put<ApiResponse<ApprovalThreshold>>(
      API_ENDPOINTS.REIMBURSEMENT.ADMIN_APPROVAL_THRESHOLD(id),
      data
    )
    return response.data
  },

  deleteApprovalThreshold: async (id: string) => {
    const response = await apiClient.delete<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.REIMBURSEMENT.ADMIN_APPROVAL_THRESHOLD(id)
    )
    return response.data
  },
}
